import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSaveReportPalette } from '../../mutations';
import { useReportPalette } from '../../queries';
import type { ReportColor } from '../../types';
import { errorMessage } from '@/shared/api/problem';
import { Button } from '@/shared/ui/atoms/Button';
import { FontIcon } from '@/shared/ui/atoms/FontIcon';
import { Input } from '@/shared/ui/atoms/Input';
import { Spinner } from '@/shared/ui/atoms/Spinner';
import { useToast } from '@/shared/ui/organisms/Toast/useToast';
import './ReportPaletteForm.styl';

const ROLES = ['primary', 'primaryDark', 'pattern', 'accent', 'accentDark', 'accentLight', 'base'] as const;
const HEX = /^#[0-9A-Fa-f]{6}$/;

const rgb = (hex: string) =>
  HEX.test(hex) ? [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)).join(', ') : '—';

/** The organisation's report palette: seven colours, each with a role in the printed reports. */
export function ReportPaletteForm({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'reportPalette' });
  const palette = useReportPalette();
  if (!palette.data) return <Spinner label={t('loading')} />;
  return <PaletteEditor initial={palette.data} canEdit={canEdit} />;
}

function PaletteEditor({ initial, canEdit }: { initial: ReportColor[]; canEdit: boolean }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'reportPalette' });
  const save = useSaveReportPalette();
  const toast = useToast();
  const [colors, setColors] = useState<ReportColor[]>(initial);
  const set = (i: number, patch: Partial<ReportColor>) => setColors(colors.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const valid = colors.every((c) => c.name.trim().length > 0 && HEX.test(c.hex));

  return (
    <div className="f-palette">
      <p className="f-palette__intro">{t('intro')}</p>
      <table className="f-palette__table">
        <thead>
          <tr>
            <th>{t('sample')}</th>
            <th>{t('name')}</th>
            <th>{t('role')}</th>
            <th>HEX</th>
            <th>RGB</th>
          </tr>
        </thead>
        <tbody>
          {colors.map((c, i) => (
            <tr key={ROLES[i]}>
              <td>
                <input type="color" className="f-palette__swatch" value={HEX.test(c.hex) ? c.hex : '#000000'} disabled={!canEdit} aria-label={t('colorOf', { name: c.name })} onChange={(e) => set(i, { hex: e.target.value.toUpperCase() })} />
              </td>
              <td>{canEdit ? <Input aria-label={t('nameOf', { n: i + 1 })} value={c.name} maxLength={60} onChange={(e) => set(i, { name: e.target.value })} /> : c.name}</td>
              <td className="f-palette__role">{t(`roles.${ROLES[i]}`)}</td>
              <td>{canEdit ? <Input aria-label={t('hexOf', { name: c.name })} value={c.hex} maxLength={7} mono invalid={!HEX.test(c.hex)} onChange={(e) => set(i, { hex: e.target.value.toUpperCase() })} /> : <strong>{c.hex}</strong>}</td>
              <td className="f-palette__rgb">{rgb(c.hex)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit ? (
        <div className="f-palette__actions">
          <Button size="sm" iconLeft={<FontIcon name="floppy-disk" />} loading={save.isPending} disabled={!valid} onClick={() => save.mutate(colors, { onSuccess: () => toast.show(t('saved'), 'success'), onError: (err) => toast.show(errorMessage(err, t('saveError')), 'error') })}>
            {t('save')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
